import { useTranslation } from "react-i18next";
import { ATTACHMENT_KIND_LABEL, isImageContentType, type AttachmentKind } from "../api/attachments";
import { formatBytes } from "../lib/format";
import { UPLOAD_STAGE_PERCENT, uploadStageLabel, type UploadRow } from "../lib/useAttachmentUpload";
import Ic from "./icons.jsx";

type Translate = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

export function attachmentKindLabel(t: Translate, kind: AttachmentKind): string {
  return t(`attachments.kind.${kind}`, ATTACHMENT_KIND_LABEL[kind] ?? kind);
}

/** Progress / error rows of `useAttachmentUpload` (attachments panel, layout background picker). */
export function UploadRows({ uploads, onRetry, onDismiss }: {
  uploads: UploadRow[];
  onRetry: (row: UploadRow) => void;
  onDismiss: (key: string) => void;
}) {
  const { t } = useTranslation();
  if (uploads.length === 0) return null;
  return (
    <ul className="att-uploads" aria-label={t("attachments.uploading", "Slanje")}>
      {uploads.map((u) => (
        <li key={u.key} className={`att-upload-row${u.status === "error" ? " is-error" : ""}`}>
          <span className="att-upload-icon">{isImageContentType(u.file.type) ? <Ic.image /> : <Ic.pdf />}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="att-name" title={u.file.name}>{u.file.name}</div>
            <div className="att-sub muted">
              {attachmentKindLabel(t, u.kind)} · {formatBytes(u.file.size)}
              {" · "}
              {u.status === "error" ? <span className="att-error">{u.error}</span> : uploadStageLabel(t, u.status)}
            </div>
            {u.status !== "error" && (
              <div className="att-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={UPLOAD_STAGE_PERCENT[u.status]} aria-label={u.file.name}>
                <div style={{ width: `${UPLOAD_STAGE_PERCENT[u.status]}%` }} />
              </div>
            )}
          </div>
          {u.status === "error" && (
            <div className="att-upload-actions">
              {u.retryable && (
                <button type="button" className="btn sm" onClick={() => onRetry(u)}>
                  <Ic.refresh /> {t("attachments.retry", "Pokušaj ponovo")}
                </button>
              )}
              <button type="button" className="icon-btn" aria-label={t("action.remove", "Ukloni")} title={t("action.remove", "Ukloni")} onClick={() => onDismiss(u.key)}>
                <Ic.x />
              </button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
